import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Copy, Loader2, Sparkles, MessageCircle, Upload, Users, CalendarDays, LayoutTemplate, History, Send, CheckCircle2 } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import SEO from "../../components/SEO";
import { TextF, AreaF, SelectF, CheckF, ImageF, StatusBadge, inputCls } from "./AFields";
import { Modal } from "./Participants";
import GreetingCard from "./GreetingCard";
import { pickLocalized } from "../../lib/localized";
import PipelineTab from "./PipelineTab";

const normalizeGreeting = (g) => ({
  ...g,
  title: pickLocalized(g.title),
  occasion: pickLocalized(g.occasion),
  category: pickLocalized(g.category) || "Festivals",
  language: pickLocalized(g.language) || "english",
  message: pickLocalized(g.message),
  message_hi: pickLocalized(g.message_hi, "hi"),
  whatsapp_message: pickLocalized(g.whatsapp_message),
  fb_caption: pickLocalized(g.fb_caption),
  quote: pickLocalized(g.quote),
});

const TABS = ["Greetings", "Create", "AI Generator", "Templates", "Contacts", "CSV Import", "WhatsApp", "Occasions", "Senders", "History", "AI Pipeline"];
const GCATS = ["Festivals", "National Days", "International Days", "Awareness Days", "Inspirational", "Birthday Wishes", "Anniversary Wishes", "Special Occasions", "Social Awareness", "Education", "Health Awareness", "Environment", "Other"];
const BLANK = { title: "", occasion: "", category: "Festivals", date: "", language: "english", message: "", message_hi: "", quote: "", quote_author: "", quote_verified: false, image: "", sender_profile_id: "", status: "draft", publish_at: "", is_daily: false };

export default function GreetingsModule() {
  const [tab, setTab] = useState(0);
  const [greetings, setGreetings] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [occasions, setOccasions] = useState([]);
  const [senders, setSenders] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [shares, setShares] = useState([]);
  const [draft, setDraft] = useState(BLANK);
  const [gFilter, setGFilter] = useState({ q: "", status: "" });
  const [ai, setAi] = useState({ prompt: "", language: "english", style: "", model: "gpt-5.4-mini" });
  const [aiResult, setAiResult] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tplEdit, setTplEdit] = useState(null);
  const [occEdit, setOccEdit] = useState(null);
  const [senderEdit, setSenderEdit] = useState(null);
  const [contactEdit, setContactEdit] = useState(null);
  const [csv, setCsv] = useState("");
  const [csvPreview, setCsvPreview] = useState(null);
  const [wa, setWa] = useState({ greeting_id: "", selected: [], queue: [], idx: 0, message: "" });

  const loadAll = () => {
    api.get(`/greetings/list?q=${encodeURIComponent(gFilter.q)}&status=${gFilter.status}`).then((r) => setGreetings(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/greetings/templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/greetings/occasions").then((r) => setOccasions(r.data)).catch(() => {});
    api.get("/greetings/senders").then((r) => setSenders(r.data)).catch(() => {});
    api.get("/greetings/contacts").then((r) => setContacts(r.data)).catch(() => {});
    api.get("/greetings/groups").then((r) => setGroups(r.data)).catch(() => {});
    api.get("/greetings/shares").then((r) => setShares(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, [gFilter.status]); // eslint-disable-line

  const sender = useMemo(() => senders.find((s) => s.id === draft.sender_profile_id) || senders.find((s) => s.is_default) || {}, [senders, draft.sender_profile_id]);
  const greetingById = (id) => greetings.find((g) => g.id === id);

  const saveGreeting = async (status) => {
    setBusy(true);
    try {
      const body = { ...draft, status: status || draft.status };
      if (body.status === "scheduled" && body.publish_at) body.publish_at = new Date(body.publish_at).toISOString();
      const r = await api.post("/greetings/save", body);
      toast.success(`Greeting ${body.status === "published" ? "published" : body.status === "scheduled" ? "scheduled" : "saved as draft"}`);
      setDraft({ ...BLANK });
      loadAll();
      setTab(0);
      return r.data;
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const runAI = async (style = "") => {
    if (!ai.prompt.trim()) { toast.error("Type a prompt first, e.g. 'Create Independence Day greeting'"); return; }
    setAiBusy(true);
    try {
      const r = await api.post("/ai/greeting", { prompt: ai.prompt, language: ai.language, style, model: ai.model, current: aiResult ? JSON.stringify(aiResult) : "" });
      setAiResult(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setAiBusy(false); }
  };

  const applyAI = () => {
    if (!aiResult) return;
    setDraft({ ...draft, title: aiResult.heading || draft.title,
      message: aiResult.message || "", message_hi: ai.language !== "english" ? (aiResult.message || "") : draft.message_hi,
      quote: aiResult.quote || "", quote_author: aiResult.quote_author || "", quote_verified: !!aiResult.quote_verified });
    toast.success("AI draft loaded into composer — review and publish manually");
    setTab(1);
  };

  const personalize = (text, contact, g) => {
    const s2 = senders.find((x) => x.id === g?.sender_profile_id) || senders.find((x) => x.is_default) || {};
    return String(text || "")
      .replaceAll("{{name}}", pickLocalized(contact?.name)).replaceAll("{{occasion}}", pickLocalized(g?.occasion))
      .replaceAll("{{date}}", g?.date || "").replaceAll("{{organisation}}", pickLocalized(s2.organisation) || "Sahaj Sarathi Foundation")
      .replaceAll("{{sender}}", pickLocalized(s2.sender_name || s2.name)).replaceAll("{{designation}}", pickLocalized(s2.designation));
  };

  const startQueue = () => {
    const g = greetingById(wa.greeting_id);
    if (!g || wa.selected.length === 0) { toast.error("Select a greeting and at least one contact"); return; }
    setWa({ ...wa, queue: wa.selected, idx: 0, message: buildWaMessage(g, contacts.find((c) => c.id === wa.selected[0])) });
  };

  const buildWaMessage = (g, contact) => {
    const main = pickLocalized(g.whatsapp_message || g.message);
    const hindi = pickLocalized(g.message_hi, "hi");
    return personalize(`Dear {{name}},\n\n${main}${hindi ? `\n\n${hindi}` : ""}\n\nRegards,\n{{sender}}{{designation}}\n{{organisation}}`, contact, g);
  };

  const openWhatsApp = async () => {
    const contact = contacts.find((c) => c.id === wa.queue[wa.idx]);
    if (!contact) return;
    const num = (contact.whatsapp || contact.mobile || "").replace(/\D/g, "");
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(wa.message)}`, "_blank");
    try { await api.post("/greetings/shares", { greeting_id: wa.greeting_id, contact_id: contact.id, contact_name: contact.name, action: "opened_whatsapp" }); } catch (e) {}
    toast.info(`Opened WhatsApp for ${contact.name}. You press Send — no automatic delivery.`);
  };

  const csvFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setCsv(String(reader.result || ""));
    reader.readAsText(f);
  };

  const runImport = async (confirm) => {
    setBusy(true);
    try {
      const r = await api.post("/greetings/contacts/import", { csv, confirm });
      if (confirm) { toast.success(`${r.data.imported} contacts imported (${r.data.duplicates} duplicates skipped)`); setCsv(""); setCsvPreview(null); loadAll(); }
      else setCsvPreview(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const TabBtn = ({ i, children }) => (
    <button onClick={() => setTab(i)} data-testid={`gtab-${i}`}
      className={`whitespace-nowrap rounded-full px-4 py-2 text-xs font-bold transition-colors ${tab === i ? "bg-navy text-white" : "border border-gray-200 bg-white text-navy"}`}>{children}</button>
  );

  return (
    <div data-testid="admin-greetings">
      <SEO title="Greetings" />
      <h1 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">Greetings & Wishes</h1>
      <p className="mt-1 text-sm text-slate-500">Create, schedule and share greetings. Nothing is published or sent automatically without your action.</p>

      <div className="mt-6 flex flex-wrap gap-2">
        {TABS.map((t2, i) => <TabBtn key={t2} i={i}>{t2}</TabBtn>)}
      </div>

      {/* TAB: GREETINGS LIST */}
      {tab === 0 && (
        <div className="mt-6">
          <div className="flex flex-wrap gap-3">
            <input value={gFilter.q} onChange={(e) => setGFilter({ ...gFilter, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && loadAll()} placeholder="Search occasion / title…" className={`${inputCls} !w-64`} data-testid="greetings-search" />
            <button onClick={loadAll} className="rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-navy" data-testid="greetings-search-btn">Search</button>
            <select value={gFilter.status} onChange={(e) => setGFilter({ ...gFilter, status: e.target.value })} className={`${inputCls} !w-44`} data-testid="greetings-status-filter">
              <option value="">All statuses</option>
              {["draft", "scheduled", "published", "archived"].map((s) => <option key={s}>{s}</option>)}
            </select>
            <button onClick={() => { setDraft(BLANK); setTab(1); }} className="btn-dark !rounded-md !px-5 !py-2.5 text-sm" data-testid="greeting-new-btn"><Plus size={15} /> Create Greeting</button>
          </div>
          <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {greetings.map((g) => (
              <div key={g.id} className="rounded-lg border border-gray-200 bg-white p-4" data-testid={`greeting-row-${g.id}`}>
                {g.image && <img src={g.image} alt={pickLocalized(g.title)} className="aspect-square w-full rounded-md object-cover" loading="lazy" />}
                <div className="mt-3 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-navy">{pickLocalized(g.title)}</p>
                    <p className="text-xs text-slate-500">{pickLocalized(g.occasion || g.category)} · {g.date || "—"}</p>
                  </div>
                  <StatusBadge value={g.status} />
                </div>
                <div className="mt-3 flex items-center gap-1.5">
                  <button onClick={() => { setDraft(normalizeGreeting(g)); setTab(1); }} className="rounded-md border border-gray-200 p-1.5 text-navy hover:border-saffron-500" aria-label="Edit" data-testid={`greeting-edit-${g.id}`}><Pencil size={13} /></button>
                  <button onClick={async () => { if (window.confirm("Delete this greeting?")) { await api.delete(`/greetings/${g.id}`); loadAll(); } }} className="rounded-md border border-gray-200 p-1.5 text-red-500 hover:border-red-400" aria-label="Delete" data-testid={`greeting-delete-${g.id}`}><Trash2 size={13} /></button>
                  {g.status === "published" && (
                    <a href={`/greetings/${g.slug}`} target="_blank" rel="noreferrer" className="ml-auto text-xs font-bold text-saffron-600" data-testid={`greeting-view-${g.id}`}>Public URL →</a>
                  )}
                </div>
              </div>
            ))}
            {greetings.length === 0 && <p className="col-span-full py-10 text-center text-sm text-slate-400">No greetings yet.</p>}
          </div>
        </div>
      )}

      {/* TAB: CREATE / COMPOSER */}
      {tab === 1 && (
        <div className="mt-6 grid gap-8 xl:grid-cols-2">
          <div className="space-y-5 rounded-lg border border-gray-200 bg-white p-6" data-testid="greeting-composer">
            <TextF label="Greeting Title" value={draft.title} onChange={(v) => setDraft({ ...draft, title: v })} required />
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Occasion</label>
                <input list="occasion-list" value={draft.occasion} onChange={(e) => setDraft({ ...draft, occasion: e.target.value })} className={inputCls} data-testid="f-occasion" />
                <datalist id="occasion-list">{occasions.map((o) => <option key={o.id} value={o.name} />)}</datalist>
              </div>
              <SelectF label="Category" value={draft.category} onChange={(v) => setDraft({ ...draft, category: v })} options={GCATS} />
              <TextF label="Date" type="date" value={draft.date} onChange={(v) => setDraft({ ...draft, date: v })} />
              <SelectF label="Language" value={draft.language} onChange={(v) => setDraft({ ...draft, language: v })} options={["english", "hindi", "bilingual"]} />
            </div>
            <AreaF label="Greeting Message (English)" value={draft.message} onChange={(v) => setDraft({ ...draft, message: v })} rows={4} />
            {(draft.language !== "english") && <AreaF label="Greeting Message (Hindi)" value={draft.message_hi} onChange={(v) => setDraft({ ...draft, message_hi: v })} rows={4} />}
            <div className="grid gap-5 sm:grid-cols-2">
              <TextF label="Quote (optional)" value={draft.quote} onChange={(v) => setDraft({ ...draft, quote: v })} />
              <TextF label="Quote Author" value={draft.quote_author} onChange={(v) => setDraft({ ...draft, quote_author: v })} />
            </div>
            <CheckF label="Quote verified as genuine (never attribute unverified quotes)" value={draft.quote_verified} onChange={(v) => setDraft({ ...draft, quote_verified: v })} />
            <div>
              <ImageF label="Card Background Image" value={draft.image} onChange={(v) => setDraft({ ...draft, image: v })} />
              <div className="mt-3 flex flex-wrap gap-2">
                {templates.slice(0, 6).map((t2) => (
                  <button key={t2.id} type="button" onClick={() => setDraft({ ...draft, image: t2.bg_image || draft.image, message: draft.message || t2.message_en, message_hi: draft.message_hi || t2.message_hi, category: t2.category })}
                    className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-navy hover:border-saffron-500" data-testid={`use-template-${t2.id}`}>
                    Use: {t2.name}
                  </button>
                ))}
              </div>
              <p className="mt-3 rounded-lg bg-navy-50/60 px-4 py-2.5 text-xs text-slate-500">AI Image Generation: <b>NOT CONFIGURED</b> — manual upload and template backgrounds always work.</p>
            </div>
            <SelectF label="Sender Profile" value={draft.sender_profile_id || (senders.find((s) => s.is_default) || {}).id || ""} onChange={(v) => setDraft({ ...draft, sender_profile_id: v })} options={senders.filter((s) => s.enabled).map((s) => s.id)} />
            <p className="-mt-3 text-xs text-slate-500">Selected: <b>{sender.name}</b> ({sender.website || "—"})</p>
            <div className="grid gap-5 sm:grid-cols-2">
              <SelectF label="Status" value={draft.status} onChange={(v) => setDraft({ ...draft, status: v })} options={["draft", "scheduled", "published", "archived"]} />
              {draft.status === "scheduled" && <TextF label="Publish At" type="datetime-local" value={draft.publish_at ? String(draft.publish_at).slice(0, 16) : ""} onChange={(v) => setDraft({ ...draft, publish_at: v })} />}
            </div>
            <CheckF label="Show as Today's Greeting (homepage)" value={draft.is_daily} onChange={(v) => setDraft({ ...draft, is_daily: v })} />
            <div className="flex flex-wrap justify-end gap-3 border-t border-gray-100 pt-5">
              <button onClick={() => saveGreeting("draft")} disabled={busy} className="rounded-md border border-gray-200 px-5 py-2.5 text-sm font-semibold text-navy" data-testid="greeting-save-draft">Save Draft</button>
              {draft.status === "scheduled"
                ? <button onClick={() => saveGreeting("scheduled")} disabled={busy} className="btn-dark !rounded-md !px-5 !py-2.5 text-sm" data-testid="greeting-schedule">{busy && <Loader2 size={14} className="animate-spin" />} Schedule</button>
                : <button onClick={() => saveGreeting("published")} disabled={busy} className="btn-primary !rounded-md !px-5 !py-2.5 text-sm disabled:opacity-50" data-testid="greeting-publish">{busy && <Loader2 size={14} className="animate-spin" />} Publish</button>}
            </div>
          </div>
          <div>
            <p className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-500">Live Card Preview (Square 1:1)</p>
            <GreetingCard greeting={draft} sender={sender} />
          </div>
        </div>
      )}

      {/* TAB: AI GENERATOR */}
      {tab === 2 && (
        <div className="mt-6 max-w-3xl space-y-5 rounded-lg border border-gray-200 bg-white p-6" data-testid="ai-generator">
          <div className="flex items-center gap-2 text-sm font-bold text-navy"><Sparkles size={16} className="text-saffron-600" /> AI Greeting Generator</div>
          <AreaF label='Prompt (e.g. "Create Independence Day greeting", "Teachers’ Day greeting in Hindi")' value={ai.prompt} onChange={(v) => setAi({ ...ai, prompt: v })} rows={2} />
          <div className="flex flex-wrap items-center gap-2">
            <SelectF label="AI Model" value={ai.model} onChange={(v) => setAi({ ...ai, model: v })} options={["gpt-5.4-mini", "gpt-5.6-terra", "gpt-5.6-luna", "gpt-5.6-sol", "gpt-5.5", "gpt-5.4", "gpt-5.2", "gpt-5.1", "gpt-5", "gpt-5-mini", "gpt-5-nano", "gpt-4.1", "gpt-4o", "claude-haiku-4-5", "claude-sonnet-4-6", "claude-sonnet-5", "claude-opus-4-8"]} />
            <SelectF label="Language" value={ai.language} onChange={(v) => setAi({ ...ai, language: v })} options={["english", "hindi", "bilingual"]} />
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => runAI()} disabled={aiBusy} className="btn-primary !rounded-md !px-5 !py-2.5 text-sm disabled:opacity-50" data-testid="ai-generate">{aiBusy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Generate</button>
            {aiResult && ["", "shorter", "formal", "inspirational"].map((s) => (
              <button key={s || "regen"} onClick={() => runAI(s)} disabled={aiBusy} className="rounded-full border border-gray-300 bg-white px-4 py-2 text-xs font-bold text-navy hover:border-saffron-500" data-testid={`ai-style-${s || "regen"}`}>
                {s === "" ? "Regenerate" : s === "shorter" ? "Make Shorter" : s === "formal" ? "Make Formal" : "Make Inspirational"}
              </button>
            ))}
          </div>
          {aiResult && (
            <div className="space-y-3 rounded-lg bg-navy-50/50 p-5" data-testid="ai-greeting-result">
              {[["Heading", "heading"], ["Message", "message"], ["Quote", "quote"], ["Quote Author", "quote_author"], ["Image Caption", "image_caption"], ["Social Caption", "social_caption"], ["WhatsApp Message", "whatsapp_message"]].map(([l, k]) => (
                aiResult[k] ? <div key={k}><p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{l}</p><p className="mt-0.5 whitespace-pre-wrap text-sm text-navy">{aiResult[k]}{k === "quote" && aiResult.quote_author ? (aiResult.quote_verified ? " (verified)" : " (UNVERIFIED — review before attributing)") : ""}</p></div> : null
              ))}
              <button onClick={applyAI} className="btn-dark !rounded-md !px-5 !py-2.5 text-sm" data-testid="ai-use-in-composer">Use in Composer (manual review before publishing)</button>
            </div>
          )}
        </div>
      )}

      {/* TAB: TEMPLATES */}
      {tab === 3 && (
        <div className="mt-6">
          <button onClick={() => setTplEdit({ name: "", category: "Festivals", message_en: "", message_hi: "", quote: "", quote_author: "", bg_image: "", is_default: false })} className="btn-dark !rounded-md !px-5 !py-2.5 text-sm" data-testid="template-new-btn"><Plus size={15} /> New Template</button>
          <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {templates.map((t2) => (
              <div key={t2.id} className="rounded-lg border border-gray-200 bg-white p-5" data-testid={`template-row-${t2.id}`}>
                <div className="flex items-center justify-between">
                  <p className="font-bold text-navy">{t2.name} {t2.is_default && <span className="ml-1 rounded-full bg-saffron-100 px-2 py-0.5 text-[10px] font-bold text-saffron-600">DEFAULT</span>}</p>
                  <span className="text-xs text-slate-400">{t2.category}</span>
                </div>
                <p className="mt-2 line-clamp-2 text-xs text-slate-500">{t2.message_en}</p>
                <div className="mt-4 flex gap-1.5">
                  <button onClick={() => setTplEdit(t2)} className="rounded-md border border-gray-200 p-1.5 text-navy hover:border-saffron-500" aria-label="Edit" data-testid={`template-edit-${t2.id}`}><Pencil size={13} /></button>
                  <button onClick={async () => { await api.post(`/greetings/templates/${t2.id}/duplicate`); loadAll(); }} className="rounded-md border border-gray-200 p-1.5 text-navy hover:border-saffron-500" aria-label="Duplicate" data-testid={`template-dup-${t2.id}`}><Copy size={13} /></button>
                  <button onClick={async () => { if (window.confirm("Delete template?")) { await api.delete(`/greetings/templates/${t2.id}`); loadAll(); } }} className="rounded-md border border-gray-200 p-1.5 text-red-500 hover:border-red-400" aria-label="Delete" data-testid={`template-del-${t2.id}`}><Trash2 size={13} /></button>
                  <button onClick={() => { setDraft({ ...BLANK, title: t2.name, category: t2.category, message: t2.message_en, message_hi: t2.message_hi, quote: t2.quote, quote_author: t2.quote_author, image: t2.bg_image }); setTab(1); }}
                    className="ml-auto rounded-md bg-saffron-100 px-3 py-1.5 text-xs font-bold text-saffron-600" data-testid={`template-use-${t2.id}`}>Use →</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB: CONTACTS */}
      {tab === 4 && (
        <div className="mt-6">
          <div className="flex flex-wrap items-center gap-3">
            <button onClick={() => setContactEdit({ name: "", mobile: "", whatsapp: "", email: "", group: "Personal", organisation: "", designation: "", language: "english", active: true })} className="btn-dark !rounded-md !px-5 !py-2.5 text-sm" data-testid="contact-new-btn"><Plus size={15} /> Add Contact</button>
            <select onChange={(e) => api.get(`/greetings/contacts?group=${encodeURIComponent(e.target.value)}`).then((r) => setContacts(r.data))} className={`${inputCls} !w-44`} data-testid="contacts-group-filter">
              <option value="">All groups</option>
              {groups.map((g) => <option key={g.id} value={g.name}>{g.name}</option>)}
            </select>
          </div>
          <p className="mt-3 text-xs text-slate-400"><Users size={12} className="mr-1 inline" />Contact lists are private and never exposed publicly.</p>
          <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="border-b border-gray-200 bg-navy-50/50 text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Mobile</th><th className="px-4 py-3">WhatsApp</th><th className="px-4 py-3">Group</th><th className="px-4 py-3">Language</th><th className="px-4 py-3 text-right">Actions</th></tr>
              </thead>
              <tbody>
                {contacts.map((c) => (
                  <tr key={c.id} className="border-b border-gray-100 last:border-0" data-testid={`contact-row-${c.id}`}>
                    <td className="px-4 py-3 font-semibold text-navy">{c.name}</td>
                    <td className="px-4 py-3">{c.mobile}</td>
                    <td className="px-4 py-3">{c.whatsapp}</td>
                    <td className="px-4 py-3">{c.group}</td>
                    <td className="px-4 py-3">{pickLocalized(c.language)}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1.5">
                        <button onClick={() => setContactEdit(c)} className="rounded-md border border-gray-200 p-1.5 text-navy hover:border-saffron-500" aria-label="Edit" data-testid={`contact-edit-${c.id}`}><Pencil size={13} /></button>
                        <button onClick={async () => { if (window.confirm("Delete contact?")) { await api.delete(`/greetings/contacts/${c.id}`); loadAll(); } }} className="rounded-md border border-gray-200 p-1.5 text-red-500 hover:border-red-400" aria-label="Delete" data-testid={`contact-del-${c.id}`}><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {contacts.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No contacts yet. Add manually or import CSV.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB: CSV IMPORT */}
      {tab === 5 && (
        <div className="mt-6 max-w-3xl space-y-5 rounded-lg border border-gray-200 bg-white p-6" data-testid="csv-import">
          <p className="text-sm text-slate-600">Columns: <code className="rounded bg-navy-50 px-1.5 py-0.5 text-xs">Name, Mobile, WhatsApp, Email, Group, Language</code>. Duplicates are detected (never overwritten silently). Review the preview, then confirm.</p>
          <label className="flex cursor-pointer items-center gap-2 rounded-md border border-gray-200 px-4 py-2.5 text-sm font-semibold text-navy hover:border-saffron-500" data-testid="csv-file-label">
            <Upload size={15} /> Choose CSV file
            <input type="file" accept=".csv" className="hidden" onChange={csvFile} data-testid="csv-file" />
          </label>
          <textarea rows={6} value={csv} onChange={(e) => setCsv(e.target.value)} placeholder={"Name,Mobile,WhatsApp,Email,Group,Language\nRamesh Kumar,9876543210,9876543210,,Volunteers,hindi"} className={`${inputCls} font-mono text-xs`} data-testid="csv-text" />
          <button onClick={() => runImport(false)} disabled={busy || !csv.trim()} className="btn-dark !rounded-md !px-5 !py-2.5 text-sm disabled:opacity-50" data-testid="csv-preview-btn">Validate & Preview</button>
          {csvPreview?.preview && (
            <div className="rounded-lg bg-navy-50/50 p-5" data-testid="csv-preview-result">
              <p className="text-sm font-bold text-navy">{csvPreview.valid_count} valid · {csvPreview.duplicates.length} duplicates · {csvPreview.invalid.length} invalid</p>
              {csvPreview.invalid.length > 0 && (
                <ul className="mt-2 text-xs text-red-500">{csvPreview.invalid.slice(0, 5).map((r) => <li key={r.row}>Row {r.row}: {r.name || "—"} ({r.reason})</li>)}</ul>
              )}
              {csvPreview.duplicates.length > 0 && (
                <ul className="mt-2 text-xs text-saffron-600">{csvPreview.duplicates.slice(0, 5).map((r) => <li key={r.row}>Row {r.row}: {r.name} — duplicate number</li>)}</ul>
              )}
              <button onClick={() => runImport(true)} disabled={busy} className="btn-primary !rounded-md mt-4 !px-5 !py-2.5 text-sm disabled:opacity-50" data-testid="csv-confirm-btn">
                {busy && <Loader2 size={14} className="animate-spin" />} Confirm Import ({csvPreview.valid_count} contacts)
              </button>
            </div>
          )}
        </div>
      )}

      {/* TAB: WHATSAPP */}
      {tab === 6 && (
        <div className="mt-6 max-w-3xl space-y-5 rounded-lg border border-gray-200 bg-white p-6" data-testid="whatsapp-panel">
          <p className="rounded-lg bg-saffron-100/60 px-4 py-2.5 text-xs text-slate-600">Simple mode: WhatsApp opens with a pre-filled message and <b>you press Send</b>. No automatic delivery is claimed. An official WhatsApp API can be added later.</p>
          {wa.queue.length === 0 ? (
            <>
              <SelectF label="Select Greeting" value={wa.greeting_id} onChange={(v) => setWa({ ...wa, greeting_id: v })} options={["", ...greetings.map((g) => g.id)]} />
              {wa.greeting_id && <p className="-mt-3 text-xs font-semibold text-saffron-600">Selected: {pickLocalized(greetingById(wa.greeting_id)?.title)}</p>}
              <div>
                <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-500">Select Contacts ({wa.selected.length})</p>
                <div className="max-h-64 space-y-1.5 overflow-y-auto rounded-md border border-gray-100 p-3">
                  {contacts.map((c) => (
                    <label key={c.id} className="flex items-center gap-2.5 text-sm text-navy">
                      <input type="checkbox" checked={wa.selected.includes(c.id)} data-testid={`wa-contact-${c.id}`}
                        onChange={(e) => setWa({ ...wa, selected: e.target.checked ? [...wa.selected, c.id] : wa.selected.filter((x) => x !== c.id) })}
                        className="h-4 w-4 accent-saffron-500" />
                      {pickLocalized(c.name)} <span className="text-xs text-slate-400">{pickLocalized(c.group)}</span>
                    </label>
                  ))}
                  {contacts.length === 0 && <p className="text-xs text-slate-400">No contacts — add them in the Contacts tab.</p>}
                </div>
              </div>
              <button onClick={startQueue} className="btn-primary !rounded-md !px-5 !py-2.5 text-sm" data-testid="wa-start-queue"><MessageCircle size={15} /> Prepare Sending Queue</button>
            </>
          ) : (
            <div className="space-y-4" data-testid="wa-queue">
              <p className="text-sm font-bold text-navy">Recipient {wa.idx + 1} of {wa.queue.length}: {pickLocalized(contacts.find((c) => c.id === wa.queue[wa.idx])?.name)}</p>
              <textarea rows={6} value={wa.message} onChange={(e) => setWa({ ...wa, message: e.target.value })} className={inputCls} data-testid="wa-message" />
              <div className="flex flex-wrap gap-3">
                <button onClick={openWhatsApp} className="btn-primary !rounded-md !px-5 !py-2.5 text-sm" data-testid="wa-open"><MessageCircle size={15} /> Open in WhatsApp</button>
                {wa.idx + 1 < wa.queue.length ? (
                  <button onClick={() => { const next = wa.idx + 1; setWa({ ...wa, idx: next, message: buildWaMessage(greetingById(wa.greeting_id), contacts.find((c) => c.id === wa.queue[next])) }); }}
                    className="btn-dark !rounded-md !px-5 !py-2.5 text-sm" data-testid="wa-next"><Send size={15} /> Next Recipient</button>
                ) : (
                  <button onClick={() => { setWa({ greeting_id: "", selected: [], queue: [], idx: 0, message: "" }); loadAll(); }} className="btn-dark !rounded-md !px-5 !py-2.5 text-sm" data-testid="wa-finish"><CheckCircle2 size={15} /> Finish Queue</button>
                )}
                <button onClick={() => setWa({ ...wa, queue: [], idx: 0 })} className="rounded-md border border-gray-200 px-4 py-2.5 text-sm font-semibold text-navy">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB: OCCASIONS */}
      {tab === 7 && (
        <div className="mt-6 max-w-3xl">
          <button onClick={() => setOccEdit({ name: "", date: "", category: "Festivals", recurring: true, language: "bilingual", active: true })} className="btn-dark !rounded-md !px-5 !py-2.5 text-sm" data-testid="occasion-new-btn"><CalendarDays size={15} /> Add Occasion</button>
          <p className="mt-3 text-xs text-slate-400">Dates are never assumed — edit every date yourself. Festival dates change each year.</p>
          <div className="mt-4 space-y-2">
            {occasions.map((o) => (
              <div key={o.id} className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3" data-testid={`occasion-row-${o.id}`}>
                <div><p className="text-sm font-bold text-navy">{o.name}</p><p className="text-xs text-slate-500">{o.date || "flexible"} · {o.category} {o.recurring ? "· recurring" : ""}</p></div>
                <div className="flex gap-1.5">
                  <button onClick={() => setOccEdit(o)} className="rounded-md border border-gray-200 p-1.5 text-navy hover:border-saffron-500" aria-label="Edit" data-testid={`occasion-edit-${o.id}`}><Pencil size={13} /></button>
                  <button onClick={async () => { if (window.confirm("Delete occasion?")) { await api.delete(`/greetings/occasions/${o.id}`); loadAll(); } }} className="rounded-md border border-gray-200 p-1.5 text-red-500 hover:border-red-400" aria-label="Delete" data-testid={`occasion-del-${o.id}`}><Trash2 size={13} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB: SENDERS */}
      {tab === 8 && (
        <div className="mt-6 max-w-3xl">
          <button onClick={() => setSenderEdit({ name: "", sender_name: "", designation: "", organisation: "", website: "", contact: "", enabled: true, is_default: false })} className="btn-dark !rounded-md !px-5 !py-2.5 text-sm" data-testid="sender-new-btn"><Plus size={15} /> Add Sender Profile</button>
          <div className="mt-4 space-y-3">
            {senders.map((s) => (
              <div key={s.id} className="rounded-lg border border-gray-200 bg-white p-5" data-testid={`sender-row-${s.id}`}>
                <div className="flex items-center justify-between">
                  <p className="font-bold text-navy">{s.name} {s.is_default && <span className="ml-1 rounded-full bg-saffron-100 px-2 py-0.5 text-[10px] font-bold text-saffron-600">DEFAULT</span>} {!s.enabled && <span className="ml-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500">DISABLED</span>}</p>
                  <div className="flex gap-1.5">
                    <button onClick={() => setSenderEdit(s)} className="rounded-md border border-gray-200 p-1.5 text-navy hover:border-saffron-500" aria-label="Edit" data-testid={`sender-edit-${s.id}`}><Pencil size={13} /></button>
                    <button onClick={async () => { if (window.confirm("Delete sender profile?")) { await api.delete(`/greetings/senders/${s.id}`); loadAll(); } }} className="rounded-md border border-gray-200 p-1.5 text-red-500 hover:border-red-400" aria-label="Delete" data-testid={`sender-del-${s.id}`}><Trash2 size={13} /></button>
                  </div>
                </div>
                <p className="mt-1 text-xs text-slate-500">{s.sender_name}{s.designation ? ` · ${s.designation}` : ""} · {s.website} · {s.contact}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB: HISTORY */}
      {tab === 9 && (
        <div className="mt-6 overflow-x-auto rounded-lg border border-gray-200 bg-white" data-testid="share-history">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead className="border-b border-gray-200 bg-navy-50/50 text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="px-4 py-3"><History size={13} className="mr-1 inline" />Time</th><th className="px-4 py-3">Greeting</th><th className="px-4 py-3">Contact</th><th className="px-4 py-3">Action</th><th className="px-4 py-3">By</th></tr>
            </thead>
            <tbody>
              {shares.map((s) => (
                <tr key={s.id} className="border-b border-gray-100 last:border-0">
                  <td className="px-4 py-2.5 text-xs text-slate-500">{String(s.at).slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 py-2.5">{pickLocalized(greetingById(s.greeting_id)?.title) || s.greeting_id.slice(0, 8)}</td>
                  <td className="px-4 py-2.5 font-semibold text-navy">{s.contact_name || "—"}</td>
                  <td className="px-4 py-2.5"><span className="rounded-full bg-navy-50 px-2.5 py-0.5 text-[11px] font-bold uppercase text-navy">{s.action.replaceAll("_", " ")}</span></td>
                  <td className="px-4 py-2.5 text-xs text-slate-500">{s.by}</td>
                </tr>
              ))}
              {shares.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No sharing actions recorded yet. External shares are recorded as prepared/copied/opened only — never as "delivered".</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* TAB: AI BROADCAST PIPELINE */}
      {tab === 10 && <PipelineTab />}

      {/* MODALS */}
      {tplEdit && (
        <Modal onClose={() => setTplEdit(null)} title="Greeting Template" testid="template-editor">
          <div className="grid gap-5 sm:grid-cols-2">
            <TextF label="Template Name" value={tplEdit.name} onChange={(v) => setTplEdit({ ...tplEdit, name: v })} required />
            <SelectF label="Category" value={tplEdit.category} onChange={(v) => setTplEdit({ ...tplEdit, category: v })} options={GCATS} />
          </div>
          <div className="mt-5 space-y-5">
            <AreaF label="Message (English)" value={tplEdit.message_en} onChange={(v) => setTplEdit({ ...tplEdit, message_en: v })} rows={3} />
            <AreaF label="Message (Hindi)" value={tplEdit.message_hi} onChange={(v) => setTplEdit({ ...tplEdit, message_hi: v })} rows={3} />
            <div className="grid gap-5 sm:grid-cols-2">
              <TextF label="Quote" value={tplEdit.quote} onChange={(v) => setTplEdit({ ...tplEdit, quote: v })} />
              <TextF label="Quote Author" value={tplEdit.quote_author} onChange={(v) => setTplEdit({ ...tplEdit, quote_author: v })} />
            </div>
            <ImageF label="Background Image" value={tplEdit.bg_image} onChange={(v) => setTplEdit({ ...tplEdit, bg_image: v })} />
            <CheckF label="Set as default template" value={tplEdit.is_default} onChange={(v) => setTplEdit({ ...tplEdit, is_default: v })} />
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => setTplEdit(null)} className="rounded-md border border-gray-200 px-5 py-2.5 text-sm font-semibold text-navy">Cancel</button>
            <button onClick={async () => { await api.post("/greetings/templates/save", tplEdit); setTplEdit(null); loadAll(); toast.success("Template saved"); }} className="btn-primary !rounded-md !px-6 !py-2.5 text-sm" data-testid="template-save">Save</button>
          </div>
        </Modal>
      )}

      {occEdit && (
        <Modal onClose={() => setOccEdit(null)} title="Occasion" testid="occasion-editor">
          <div className="grid gap-5 sm:grid-cols-2">
            <TextF label="Occasion" value={occEdit.name} onChange={(v) => setOccEdit({ ...occEdit, name: v })} required />
            <TextF label="Date" type="date" value={occEdit.date} onChange={(v) => setOccEdit({ ...occEdit, date: v })} />
            <SelectF label="Category" value={occEdit.category} onChange={(v) => setOccEdit({ ...occEdit, category: v })} options={["Festivals", "National Festivals", "Cultural Festivals", "National Days", "International Days", "Awareness Days", "Education Days", "Health Days", "Environment Days", "Social Justice Days", "Foundation Events", "Inspirational", "Custom"]} />
            <SelectF label="Language" value={occEdit.language} onChange={(v) => setOccEdit({ ...occEdit, language: v })} options={["english", "hindi", "bilingual"]} />
            <CheckF label="Recurring (yearly)" value={occEdit.recurring} onChange={(v) => setOccEdit({ ...occEdit, recurring: v })} />
            <CheckF label="Active" value={occEdit.active} onChange={(v) => setOccEdit({ ...occEdit, active: v })} />
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => setOccEdit(null)} className="rounded-md border border-gray-200 px-5 py-2.5 text-sm font-semibold text-navy">Cancel</button>
            <button onClick={async () => { await api.post("/greetings/occasions/save", occEdit); setOccEdit(null); loadAll(); toast.success("Occasion saved"); }} className="btn-primary !rounded-md !px-6 !py-2.5 text-sm" data-testid="occasion-save">Save</button>
          </div>
        </Modal>
      )}

      {senderEdit && (
        <Modal onClose={() => setSenderEdit(null)} title="Sender Profile" testid="sender-editor">
          <div className="grid gap-5 sm:grid-cols-2">
            <TextF label="Profile Name" value={senderEdit.name} onChange={(v) => setSenderEdit({ ...senderEdit, name: v })} required />
            <TextF label="Sender Name" value={senderEdit.sender_name} onChange={(v) => setSenderEdit({ ...senderEdit, sender_name: v })} />
            <TextF label="Designation" value={senderEdit.designation} onChange={(v) => setSenderEdit({ ...senderEdit, designation: v })} />
            <TextF label="Organisation" value={senderEdit.organisation} onChange={(v) => setSenderEdit({ ...senderEdit, organisation: v })} />
            <TextF label="Website" value={senderEdit.website} onChange={(v) => setSenderEdit({ ...senderEdit, website: v })} />
            <TextF label="Contact Number" value={senderEdit.contact} onChange={(v) => setSenderEdit({ ...senderEdit, contact: v })} />
            <CheckF label="Enabled" value={senderEdit.enabled} onChange={(v) => setSenderEdit({ ...senderEdit, enabled: v })} />
            <CheckF label="Set as default" value={senderEdit.is_default} onChange={(v) => setSenderEdit({ ...senderEdit, is_default: v })} />
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => setSenderEdit(null)} className="rounded-md border border-gray-200 px-5 py-2.5 text-sm font-semibold text-navy">Cancel</button>
            <button onClick={async () => { await api.post("/greetings/senders/save", senderEdit); setSenderEdit(null); loadAll(); toast.success("Sender saved"); }} className="btn-primary !rounded-md !px-6 !py-2.5 text-sm" data-testid="sender-save">Save</button>
          </div>
        </Modal>
      )}

      {contactEdit && (
        <Modal onClose={() => setContactEdit(null)} title="Contact" testid="contact-editor">
          <div className="grid gap-5 sm:grid-cols-2">
            <TextF label="Name" value={contactEdit.name} onChange={(v) => setContactEdit({ ...contactEdit, name: v })} required />
            <TextF label="Mobile" value={contactEdit.mobile} onChange={(v) => setContactEdit({ ...contactEdit, mobile: v })} />
            <TextF label="WhatsApp" value={contactEdit.whatsapp} onChange={(v) => setContactEdit({ ...contactEdit, whatsapp: v })} />
            <TextF label="Email" value={contactEdit.email} onChange={(v) => setContactEdit({ ...contactEdit, email: v })} />
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Group</label>
              <input list="group-list" value={contactEdit.group} onChange={(e) => setContactEdit({ ...contactEdit, group: e.target.value })} className={inputCls} data-testid="f-group" />
              <datalist id="group-list">{groups.map((g) => <option key={g.id} value={g.name} />)}</datalist>
            </div>
            <SelectF label="Language" value={contactEdit.language} onChange={(v) => setContactEdit({ ...contactEdit, language: v })} options={["english", "hindi"]} />
            <TextF label="Organisation" value={contactEdit.organisation} onChange={(v) => setContactEdit({ ...contactEdit, organisation: v })} />
            <TextF label="Designation" value={contactEdit.designation} onChange={(v) => setContactEdit({ ...contactEdit, designation: v })} />
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => setContactEdit(null)} className="rounded-md border border-gray-200 px-5 py-2.5 text-sm font-semibold text-navy">Cancel</button>
            <button onClick={async () => {
              try {
                await api.post("/greetings/contacts/save", contactEdit);
                if (contactEdit.group && !groups.some((g) => g.name.toLowerCase() === contactEdit.group.toLowerCase())) {
                  await api.post("/greetings/groups/save", { name: contactEdit.group });
                }
                setContactEdit(null); loadAll(); toast.success("Contact saved");
              } catch (e) { toast.error(errMsg(e)); }
            }} className="btn-primary !rounded-md !px-6 !py-2.5 text-sm" data-testid="contact-save">Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
